import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Alert,
    alpha,
    Skeleton,
    Fade,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppleIcon from '@mui/icons-material/Apple';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// =============================================================================
// TAP APPLE PAY CONFIGURATION
// =============================================================================
const APPLE_PAY_CONFIG = {
    publicKey: process.env.REACT_APP_TAP_PUBLIC_KEY || 'pk_test_zpZr9J7P0dCRL8FsUeWh52GO',
    merchantId: process.env.REACT_APP_TAP_APPLE_PAY_MERCHANT_ID || process.env.REACT_APP_TAP_MERCHANT_ID || 'merchant_id',
    merchantDomain: process.env.REACT_APP_TAP_APPLE_PAY_DOMAIN || window.location.hostname,
    sdkCss: 'https://tap-sdks.b-cdn.net/apple-pay/build-1.0.19/main.css',
    sdkJs: 'https://tap-sdks.b-cdn.net/apple-pay/build-1.0.19/main.js',
};

// =============================================================================
// TAP APPLE PAY COMPONENT
// =============================================================================
const TapApplePay = forwardRef(({
    amount,
    currency = 'SAR',
    customerEmail,
    customerPhone,
    customerName,
    customerId,
    onTokenSuccess,
    onTokenError,
    onReady,
    onCancel,
    onClick,
    disabled = false,
    buttonType = 'pay', // 'pay', 'buy', 'plain', 'order', 'donate', 'continue', 'check-out'
    buttonTheme = 'dark', // 'dark', 'light', 'outline'
}, ref) => {
    const { t, i18n } = useTranslation();
    const containerIdRef = useRef(`tap-apple-pay-${Math.random().toString(36).substr(2, 9)}`);
    const initializedRef = useRef(false);
    const unmountRef = useRef(false);

    const [sdkReady, setSdkReady] = useState(false);
    const [sdkLoading, setSdkLoading] = useState(true);
    const [isSupported, setIsSupported] = useState(null); // null = checking, true = supported, false = not supported
    const [error, setError] = useState(null);

    // =============================================================================
    // EXPOSE METHODS VIA REF
    // =============================================================================
    useImperativeHandle(ref, () => ({
        isReady: () => sdkReady,
        isSupported: () => isSupported,
        getError: () => error,
    }));

    // =============================================================================
    // CHECK APPLE PAY SUPPORT
    // =============================================================================
    const checkApplePaySupport = useCallback(() => {
        // Check if Apple Pay is available in the browser
        if (window.ApplePaySession && window.ApplePaySession.canMakePayments) {
            try {
                const canMakePayments = window.ApplePaySession.canMakePayments();
                return canMakePayments;
            } catch (e) {
                console.warn('Apple Pay check failed:', e);
                return false;
            }
        }
        return false;
    }, []);

    // =============================================================================
    // LOAD TAP APPLE PAY SDK
    // =============================================================================
    const loadApplePaySDK = useCallback(() => {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            if (window.TapApplepaySDK) {
                resolve(window.TapApplepaySDK);
                return;
            }

            // Load CSS
            const existingCss = document.querySelector(`link[href="${APPLE_PAY_CONFIG.sdkCss}"]`);
            if (!existingCss) {
                const cssLink = document.createElement('link');
                cssLink.rel = 'stylesheet';
                cssLink.href = APPLE_PAY_CONFIG.sdkCss;
                document.head.appendChild(cssLink);
            }

            // Load JS
            const existingScript = document.querySelector(`script[src="${APPLE_PAY_CONFIG.sdkJs}"]`);
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(window.TapApplepaySDK));
                existingScript.addEventListener('error', reject);
                return;
            }

            const script = document.createElement('script');
            script.src = APPLE_PAY_CONFIG.sdkJs;
            script.async = true;
            script.onload = () => resolve(window.TapApplepaySDK);
            script.onerror = () => reject(new Error('Failed to load Tap Apple Pay SDK'));
            document.head.appendChild(script);
        });
    }, []);

    // =============================================================================
    // INITIALIZE APPLE PAY BUTTON
    // =============================================================================
    const initializeApplePay = useCallback(async () => {
        if (initializedRef.current || unmountRef.current) return;

        try {
            setSdkLoading(true);
            setError(null);

            // First check if Apple Pay is supported
            const supported = checkApplePaySupport();
            setIsSupported(supported);

            if (!supported) {
                setSdkLoading(false);
                return;
            }

            // Load SDK
            const sdk = await loadApplePaySDK();
            if (unmountRef.current) return;

            if (!sdk) {
                throw new Error('Apple Pay SDK not available');
            }

            const {
                render,
                ThemeMode,
                SupportedNetworks,
                Scope,
                Environment,
                Locale,
                ButtonType,
                Edges
            } = sdk;

            // Parse customer name
            const nameParts = customerName?.split(' ') || ['', ''];
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            // Parse phone
            const phoneNumber = customerPhone?.replace(/^\+/, '') || '';
            const countryCode = phoneNumber.startsWith('966') ? '966' : '966';
            const localNumber = phoneNumber.startsWith('966') ? phoneNumber.slice(3) : phoneNumber;

            // Determine environment
            const isProduction = process.env.NODE_ENV === 'production' &&
                !APPLE_PAY_CONFIG.publicKey.includes('test');
            const environment = isProduction ? Environment.Production : Environment.Development;

            // Map button type
            const buttonTypeMap = {
                'pay': ButtonType.PAY,
                'buy': ButtonType.BUY,
                'plain': ButtonType.PLAIN,
                'order': ButtonType.ORDER,
                'donate': ButtonType.DONATE,
                'continue': ButtonType.CONTINUE,
                'check-out': ButtonType.CHECKOUT,
            };

            // Map theme
            const themeMap = {
                'dark': ThemeMode.DARK,
                'light': ThemeMode.LIGHT,
            };

            // Wait for container to be in DOM
            await new Promise(resolve => setTimeout(resolve, 100));

            const container = document.getElementById(containerIdRef.current);
            if (!container || unmountRef.current) return;

            // Render Apple Pay button
            render(
                {
                    publicKey: APPLE_PAY_CONFIG.publicKey,
                    environment: environment,
                    scope: Scope.TapToken,
                    merchant: {
                        domain: APPLE_PAY_CONFIG.merchantDomain,
                        id: APPLE_PAY_CONFIG.merchantId
                    },
                    transaction: {
                        currency: currency,
                        amount: String(amount)
                    },
                    acceptance: {
                        supportedBrands: [
                            SupportedNetworks.Mada,
                            SupportedNetworks.Visa,
                            SupportedNetworks.MasterCard,
                            SupportedNetworks.Amex
                        ],
                        supportedCardsWithAuthentications: ['3DS']
                    },
                    customer: {
                        ...(customerId && { id: customerId }),
                        name: [
                            {
                                locale: i18n.language === 'ar' ? Locale.AR : Locale.EN,
                                first: firstName,
                                last: lastName,
                                middle: ''
                            }
                        ],
                        contact: {
                            email: customerEmail || '',
                            phone: {
                                countryCode: countryCode,
                                number: localNumber
                            }
                        }
                    },
                    interface: {
                        locale: i18n.language === 'ar' ? Locale.AR : Locale.EN,
                        theme: themeMap[buttonTheme] || ThemeMode.DARK,
                        type: buttonTypeMap[buttonType] || ButtonType.PAY,
                        edges: Edges.CURVED
                    },
                    onCancel: () => {
                        console.log('Apple Pay cancelled');
                        onCancel?.();
                    },
                    onError: (err) => {
                        console.error('Apple Pay error:', err);
                        setError(err?.message || t('Apple Pay error occurred'));
                        onTokenError?.(err);
                    },
                    onSuccess: async (tokenData) => {
                        console.log('Apple Pay token received:', tokenData);
                        onTokenSuccess?.(tokenData);
                    },
                    onReady: () => {
                        console.log('Apple Pay ready');
                        setSdkReady(true);
                        setSdkLoading(false);
                        onReady?.();
                    },
                    onClick: () => {
                        console.log('Apple Pay clicked');
                        onClick?.();
                    }
                },
                containerIdRef.current
            );

            initializedRef.current = true;

        } catch (err) {
            console.error('Apple Pay initialization error:', err);
            if (!unmountRef.current) {
                setError(err.message || t('Failed to initialize Apple Pay'));
                setSdkLoading(false);
                setIsSupported(false);
            }
        }
    }, [
        amount,
        currency,
        customerEmail,
        customerPhone,
        customerName,
        customerId,
        buttonType,
        buttonTheme,
        i18n.language,
        t,
        checkApplePaySupport,
        loadApplePaySDK,
        onTokenSuccess,
        onTokenError,
        onReady,
        onCancel,
        onClick
    ]);

    // =============================================================================
    // EFFECTS
    // =============================================================================
    useEffect(() => {
        unmountRef.current = false;
        initializeApplePay();

        return () => {
            unmountRef.current = true;
        };
    }, [initializeApplePay]);

    // =============================================================================
    // RENDER - NOT SUPPORTED STATE
    // =============================================================================
    if (isSupported === false) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                }}
            >
                <Stack spacing={2} alignItems="center" textAlign="center">
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <AppleIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                            {t('Apple Pay Not Available')}
                        </Typography>
                        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                            {t('Apple Pay is only available on Safari browser on Apple devices.')}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>
        );
    }

    // =============================================================================
    // RENDER - LOADING STATE
    // =============================================================================
    if (sdkLoading || isSupported === null) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box flex={1}>
                            <Skeleton variant="text" width={120} height={24} />
                            <Skeleton variant="text" width={180} height={16} />
                        </Box>
                    </Stack>
                    <Skeleton
                        variant="rounded"
                        height={48}
                        sx={{ borderRadius: 3 }}
                    />
                </Stack>
            </Paper>
        );
    }

    // =============================================================================
    // RENDER - READY STATE
    // =============================================================================
    return (
        <Fade in timeout={500}>
            <Box sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: 'grey.900',
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.02),
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                bgcolor: 'grey.900',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <AppleIcon sx={{ fontSize: 28, color: 'white' }} />
                        </Box>
                        <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('Apple Pay')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('Fast and secure payment with Apple Pay')}
                            </Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <SecurityIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography variant="caption" color="success.main" fontWeight={500}>
                                {t('Secure')}
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Apple Pay Button Container */}
                    <Box
                        id={containerIdRef.current}
                        sx={{
                            minHeight: 48,
                            borderRadius: 3,
                            overflow: 'hidden',
                            '& > div': {
                                width: '100% !important',
                            },
                            '& apple-pay-button': {
                                width: '100%',
                                height: '48px',
                                '--apple-pay-button-width': '100%',
                                '--apple-pay-button-height': '48px',
                                '--apple-pay-button-border-radius': '12px',
                            },
                        }}
                    />

                    {/* Error Message */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mt: 2, borderRadius: 2 }}
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Features */}
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                        sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">
                                {t('No card number entry')}
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">
                                {t('Face ID / Touch ID')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Paper>
            </Box>
        </Fade>
    );
});

TapApplePay.displayName = 'TapApplePay';

export default TapApplePay;
